import { Clock, User, AlertCircle, Activity, FileText } from 'lucide-react';

export function CaseDetailPanel({ caseData, session }) {
  if (!caseData) return null;

  const { title, difficulty, category, is_emergency, max_duration_sec, patient, chief_complaint, rubric } = caseData;

  const difficultyColors = {
    Beginner: 'bg-violet-100 text-violet-700',
    Intermediate: 'bg-amber-100 text-amber-700',
    Advanced: 'bg-red-100 text-red-700',
  };

  return (
    <div className="bg-white pb-6">
      {/* Header */}
      <div className="p-5 pb-4 border-b border-slate-100">
        <div className="flex items-center gap-2 mb-2">
          <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${difficultyColors[difficulty] || difficultyColors.Beginner}`}>
            {difficulty}
          </span>
          {is_emergency && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-50 text-red-600 border border-red-100 text-[10px] font-bold uppercase tracking-wider">
              <AlertCircle className="w-3 h-3" /> ER
            </span>
          )}
          <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[10px] font-bold uppercase tracking-wider">
            {category}
          </span>
        </div>
        
        <h2 className="text-lg font-bold text-slate-800 leading-tight mb-1.5">{title}</h2>
        <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
          <Clock className="w-3.5 h-3.5" />
          {Math.floor(max_duration_sec / 60)} minutes time limit
        </div>
      </div>

      <div className="p-5 space-y-6">
        {/* Patient Profile */}
        <section>
          <h3 className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500 mb-2 uppercase tracking-wider">
            <User className="w-3.5 h-3.5 text-violet-500" /> Patient Profile
          </h3>
          <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
            <div className="flex items-center gap-3 mb-2 pb-2 border-b border-slate-200">
              <div className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-500 font-bold text-sm shadow-sm">
                {patient.initials || 'PT'}
              </div>
              <div>
                <p className="text-sm font-bold text-slate-800">{patient.name || 'Unknown Patient'}</p>
                <div className="flex flex-wrap gap-1 mt-0.5">
                  <span className="text-[10px] font-bold text-slate-600 bg-slate-200/60 px-1.5 py-0.5 rounded uppercase">{patient.age}y</span>
                  <span className="text-[10px] font-bold text-slate-600 bg-slate-200/60 px-1.5 py-0.5 rounded uppercase">{patient.gender}</span>
                  <span className="text-[10px] font-bold text-slate-600 bg-slate-200/60 px-1.5 py-0.5 rounded uppercase">{patient.language}</span>
                  {patient.mood && <span className="text-[10px] font-bold text-slate-600 bg-slate-200/60 px-1.5 py-0.5 rounded uppercase">{patient.mood}</span>}
                  {caseData.literacy_level && <span className="text-[10px] font-bold text-slate-600 bg-slate-200/60 px-1.5 py-0.5 rounded uppercase">{caseData.literacy_level} Lit.</span>}
                </div>
              </div>
            </div>
            
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Chief Complaint</p>
              <p className="text-[13px] font-medium text-slate-700 leading-snug italic">
                "{chief_complaint}"
              </p>
            </div>
          </div>
        </section>

        {/* Evaluation Rubric */}
        <section>
          <h3 className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500 mb-2 uppercase tracking-wider">
            <Activity className="w-3.5 h-3.5 text-violet-500" /> Evaluation Focus
          </h3>
          <div className="grid grid-cols-2 gap-2">
            <RubricItem label="History" weight={rubric.history_taking} />
            <RubricItem label="Diagnosis" weight={rubric.diagnosis} />
            <RubricItem label="Rx Accuracy" weight={rubric.rx_accuracy} />
            <RubricItem label="Investigations" weight={rubric.investigations} />
            <RubricItem label="Communication" weight={rubric.communication} />
            <RubricItem label="Red Flags" weight={rubric.red_flag_handling} />
          </div>
        </section>

        {/* Instructions */}
        <section>
          <h3 className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500 mb-2 uppercase tracking-wider">
            <FileText className="w-3.5 h-3.5 text-violet-500" /> Instructions
          </h3>
          <ul className="space-y-1.5 text-xs font-medium text-slate-600">
            <li className="flex gap-1.5">
              <span className="text-violet-500 font-bold">1.</span>
              Conduct a thorough patient history and assessment via voice call.
            </li>
            <li className="flex gap-1.5">
              <span className="text-violet-500 font-bold">2.</span>
              Identify key symptoms and any red flags.
            </li>
            <li className="flex gap-1.5">
              <span className="text-violet-500 font-bold">3.</span>
              After the call, submit a formal prescription and treatment plan.
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
}

function RubricItem({ label, weight }) {
  if (!weight) return null;
  return (
    <div className="flex items-center justify-between p-2 bg-white border border-slate-200 rounded-lg shadow-sm">
      <span className="text-[11px] font-bold text-slate-600">{label}</span>
      <span className="text-[11px] font-bold text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded">{weight}%</span>
    </div>
  );
}
